// @ts-check
import _ from 'lodash'

/**
 * Traverse the given object or array, calling the given keyName function on each item.
 * @param {object|Array} data - The data to process (object or array).
 * @param {string} keyName - A property name mapping to a function that will be called on the data.
 * @param {boolean} collectionFunction - Whether to call the keyName function on collections.
 * @param {object} originalData - The original data object
 * @returns {object|Array} The original data object or array.
 */
export function traverse (data, keyName, collectionFunction = false, originalData = this?.originalData) {
  const dataKey = data?.key
  if (data && data.isCollection) {
    if (collectionFunction) {
      data = data[keyName]?.(originalData)
    } else {
      data = data.data
    }
  }
  if (data instanceof Array) {
    data = data.map((item, index) => {
      if (_.isFinite(item?.id)) {
        return traverse(item, keyName, collectionFunction, _.find(originalData, { id: item.id }))
      } else {
        return traverse(item, keyName, collectionFunction, originalData?.[index])
      }
    })
  } else if (data instanceof Object) {
    if (data[keyName]) {
      data = data[keyName]?.(originalData?.[dataKey])
    }
    Object.keys(data).forEach(key => {
      data[key] = traverse(data[key], keyName, collectionFunction, originalData?.[key])
    })
  }

  return data
}

/**
 * Traverse the given object or array, applying the given process function to each item.
 * @param {object|Array} data - The data to process (object or array)
 * @param {function} process - The processing function
 * @returns {object|Array} The original data object or array.
 */
export function traverseProcess (data, process) {
  if (data instanceof Array) {
    data = data.map(item => traverseProcess(item, process))
  } else if (data instanceof Object) {
    data = process(data)
    Object.keys(data).forEach(key => { data[key] = traverseProcess(data[key], process) })
  }

  return data
}

/**
 * @param {any} current
 * @param {any} original
 * @param {string} [requiredKey] - For objects, always includes this key in the result even if it is not changed.
 * @returns A new object with only changed values if given object, otherwise the current value or undefined if the values are equal.
 */
export function changes (current, original, requiredKey = null) {
  if (_.isObject(current) || _.isObject(original)) {
    return _.transform(current, (result, value, key) => {
      if (!_.isEqual(value, original[key])) {
        result[key] = _.isObject(value) && _.isObject(original[key]) ? changes(value, original[key], requiredKey) : value
      } else if (key === requiredKey) {
        result[key] = current[key]
      }
    })
  } else {
    return current === original ? undefined : current
  }
}

/**
 * @param {object} original
 * @param {object} other
 * @returns {object} A new object.
 */
export function mergeById (original, other) {
  const mergedHashes = _.merge(recursivelyKeyArrays(original), recursivelyKeyArrays(other))
  return decodeKeyedArrays(mergedHashes)
}

/**
 * @param {object} object
 * @returns A new object.
 */
export function decodeKeyedArrays (object) {
  return _.transform(object, (result, value, key) => {
    if (value && value._keyedArray) {
      delete value._keyedArray
      result[key] = _.values(value)
    } else {
      result[key] = value
    }
  })
}

/**
 * @param {object} object
 * @param {string} [keyBy]
 * @returns A new object.
 */
export function recursivelyKeyArrays (object, keyBy = 'id') {
  return _.reduce(object, (result, value, key) => {
    if (_.isArray(value) && value.length && value[value.length - 1][keyBy]) {
      result[key] = _.keyBy(value, keyBy)
      delete result[key].undefined
      result[key]._keyedArray = true

      // Check all children recursively
      _.each(value, entry => _.isObject(entry) && !_.isArray(entry) && recursivelyKeyArrays(entry, keyBy))
    } else {
      result[key] = value
    }

    return result
  }, {})
}

/**
 * Remove the id property from the given data object in place if it is a local id.
 * A local id is an id less than 0.
 * @param {object} data
 * @returns {object} The original data object.
 */
export function withoutLocalIds (data) {
  if (data && data.id < 0) {
    data = _.clone(data)
    delete data.id
  }
  return data
}
